import { mergeUserData, useAuthContext } from '@aibees/shared/auth';
import {
  CustomFile,
  CustomPopover,
  Icon,
  UploadAvatar,
  UploadAvatarRef,
  UploadProps,
  usePopover,
} from '@aibees/shared/components';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { classnames, resize } from '@aibees/shared/utils';

import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import {
  changeAvatarCallbackRequest,
  useChangeProfileAvatarMutationApi,
} from '@aibees/shared/features/user-profile/hooks/use-change-profile-avatar-mutation-api';
import { useUserEditMutationApi } from '@aibees/shared/features/user-profile/hooks/use-user-edit-mutation-api';
import { useUserProfileApi } from '@aibees/shared/features/user-profile/hooks/use-user-profile-api';
import { MenuItem, Stack, Typography } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useEffect, useRef, useState } from 'react';
import * as Styled from './styles';
import { UploadProfileAvatarProps as Props } from './types';

export const UploadProfileAvatar = memo(({ className }: Props) => {
  const popover = usePopover();
  const notify = useNotify();
  const deleting = useBoolean();
  const loading = useBoolean();
  const { setUser } = useAuthContext();
  const { data: userData } = useUserProfileApi();
  const api = useChangeProfileAvatarMutationApi();
  const editProfileApi = useUserEditMutationApi();
  const avatarRef = useRef<UploadAvatarRef>(null);
  const [url, setUrl] = useState(userData?.attributes?.avatar);
  const [file, setFile] = useState<UploadProps['file']>(
    userData?.attributes?.avatar
  );
  const hasImg = !!url;

  useEffect(() => {
    // NOTE: updated_at is added just for refreshing the image link as the link is the same for each user
    setFile(`${userData?.attributes?.avatar}?updated_at=${Date.now()}`);
    setUrl(userData?.attributes?.avatar);
  }, [userData?.attributes?.avatar]);

  const handleChange = () => {
    avatarRef.current?.open();
    popover.onClose();
  };

  const handleDelete = () => {
    popover.onClose();
    deleting.onTrue();
    editProfileApi
      .mutateAsync(
        mergeUserData(
          userData,
          {},
          { attributes: { ...userData?.attributes, ...{ avatar: '' } } }
        )
      )
      .then((res) => {
        setUser(res);
        setFile(undefined);
        setUrl('');
      })
      .finally(() => {
        notify.complete(
          'Profile photo removed',
          'Your profile picture has been deleted. The default avatar will now be displayed. You can upload a new photo anytime in your profile settings.',
          random(1, 100),
          {
            icon: notificationIconMap.Profile!,
          }
        );
        deleting.onFalse();
      });
  };

  const handleUploadAndSetAvatar = (avatarFile: CustomFile) => {
    api.mutateAsync(
      { file: avatarFile },
      {
        onSuccess: (response, request) => {
          changeAvatarCallbackRequest(response.url, request.file)
            .then(() => {
              // NOTE: updated_at is added just for refreshing the image link as the link is the same for each user
              setFile(
                `${userData?.attributes?.avatar}?updated_at=${Date.now()}`
              );
              setUrl(userData?.attributes?.avatar);
              if (userData) {
                setUser({
                  ...userData,
                  ...{
                    attributes: {
                      ...userData?.attributes,
                      ...{
                        avatar: `${
                          userData?.attributes?.avatar
                        }?updated_at=${Date.now()}`,
                      },
                    },
                  },
                });
              }
            })
            .finally(() => {
              notify.complete(
                'Profile photo updated',
                'Your profile photo has been uploaded and is now visible. If you don’t see it right away, refresh the page.',
                random(1, 100),
                {
                  icon: notificationIconMap.Profile!,
                }
              );
              loading.onFalse();
            });
        },
      }
    );
  };

  const handleDrop = (acceptedFiles: File[]) => {
    loading.onTrue();
    const tmp = acceptedFiles[0];
    if (!tmp) return;
    resize(tmp).then((resized) => {
      const preview = URL.createObjectURL(resized);
      setUrl(preview);
      setFile(Object.assign(resized, { preview }));
      handleUploadAndSetAvatar(Object.assign(resized, { preview }));
    });
  };

  const uploader = (
    <Styled.UploadWrapper>
      <UploadAvatar
        loading={loading.value || deleting.value}
        // percentage={Number(percentage)}
        onDrop={handleDrop}
        noClick={hasImg}
        file={url ? file : undefined}
        ref={avatarRef}
        helperText={!url}
        disabled={loading.value}
      />
    </Styled.UploadWrapper>
  );

  return (
    <Styled.Wrapper className={classnames('UploadProfileAvatar', className)}>
      <Styled.Card>
        {hasImg ? (
          <CustomPopover
            state={popover}
            arrowPosition="top-left"
            element={uploader}
          >
            <MenuItem onClick={handleChange}>
              <Stack direction="row" gap={1} alignItems="center">
                <Icon name="collections" size={16} />{' '}
                <Typography variant="body3" fontWeight={600} color="grey.700">
                  Change photo
                </Typography>
              </Stack>
            </MenuItem>
            <MenuItem onClick={handleDelete}>
              <Stack direction="row" gap={1} alignItems="center">
                <Icon name="delete" size={16} />{' '}
                <Typography variant="body3" fontWeight={600} color="grey.700">
                  Delete photo
                </Typography>
              </Stack>
            </MenuItem>
          </CustomPopover>
        ) : (
          uploader
        )}
      </Styled.Card>
    </Styled.Wrapper>
  );
});

UploadProfileAvatar.displayName = 'UploadProfileAvatar';
